import { intl } from "dmsbasereact";
import React, { useRef, useState, useCallback } from "react";
import { Chip, Typography, Box, Grid, Tooltip, CircularProgress, Paper } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import PrintIcon from "@mui/icons-material/Print";
import dayjs from "dayjs";

import {
    DmsPage,
    DmsTable,
    DmsAltBox,
    DmsModal,
    DmsButton,
    DmsTextField,
    DmsTabber,
    fetchData,
    HTTP_STATUS,
    Notifications
} from "dmsbasereact";

import { DispensingStatus, DispensingStatusEnum, getDispensingStatusOptions } from '../../constants/DispensingStatus';
import { CancellationRequestStatus, CancellationRequestStatusEnum } from '../../constants/CancellationRequestStatus';
import { openDispensingReceiptForPrint } from '../../utils/downloadDispensingReceipt';

const getStatusChip = (status) => {
    return (
        <Chip
            label={DispensingStatus.getText(status)}
            color={DispensingStatus.getColor(status)}
            size="small"
        />
    );
};

const getCancellationRequestChip = (cancellationRequest) => {
    if (!cancellationRequest) {
        return '-';
    }
    return (
        <Chip
            label={CancellationRequestStatus.getText(cancellationRequest.status)}
            color={CancellationRequestStatus.getColor(cancellationRequest.status)}
            size="small"
        />
    );
};

// Only DISPENSED dispensations can be canceled, and only if no pending cancellation request exists
const canCancel = (dispensation) => {
    if (dispensation?.status !== DispensingStatusEnum.DISPENSED) {
        return false;
    }
    // Check if there's a pending cancellation request
    if (dispensation?.cancellation_request?.status === CancellationRequestStatusEnum.PENDING) {
        return false;
    }
    return true;
};

export const DmsDispensationHistoryPage = () => {
    const table = useRef(null);

    // Modal states
    const [selectedDispensation, setSelectedDispensation] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);

    // Cancel modal states
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [dispensationToCancel, setDispensationToCancel] = useState(null);
    const [cancelJustification, setCancelJustification] = useState('');
    const [canceling, setCanceling] = useState(false);

    // History state
    const [historyData, setHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Cancellation requests state
    const [cancellationRequests, setCancellationRequests] = useState([]);
    const [loadingCancellationRequests, setLoadingCancellationRequests] = useState(false);

    // Detail batches state (multi-batch from DispensingBatch junction)
    const [detailBatches, setDetailBatches] = useState([]);

    // Fetch history when modal opens
    const fetchHistory = useCallback(async (pk) => {
        if (!pk) return;
        setLoadingHistory(true);
        try {
            const response = await fetchData(`/dispensing/${pk}/history`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setHistoryData(response.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            setHistoryData([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    // Fetch cancellation requests when modal opens
    const fetchCancellationRequests = useCallback(async (pk) => {
        if (!pk) return;
        setLoadingCancellationRequests(true);
        try {
            const response = await fetchData(`/dispensing/${pk}/cancellation-requests`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setCancellationRequests(response.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching cancellation requests:', error);
            setCancellationRequests([]);
        } finally {
            setLoadingCancellationRequests(false);
        }
    }, []);

    // Handlers
    const handleViewDetails = useCallback(async (dispensation) => {
        setSelectedDispensation(dispensation);
        setDetailModalOpen(true);
        fetchHistory(dispensation.pk);
        fetchCancellationRequests(dispensation.pk);

        // Fetch multi-batch data for detail view
        try {
            const batchesResponse = await fetchData(
                `/dispensing/${dispensation.pk}/dispensing-batches`, 'GET', true
            );
            if (batchesResponse.status === HTTP_STATUS.OK_200) {
                setDetailBatches(batchesResponse.data?.data || []);
            } else {
                setDetailBatches([]);
            }
        } catch {
            setDetailBatches([]);
        }
    }, [fetchHistory, fetchCancellationRequests]);

    // Cancel handlers
    const handleOpenCancelModal = useCallback((dispensation) => {
        setDispensationToCancel(dispensation);
        setCancelJustification('');
        setCancelModalOpen(true);
    }, []);

    const handleCloseCancelModal = useCallback(() => {
        setCancelModalOpen(false);
        setDispensationToCancel(null);
        setCancelJustification('');
    }, []);

    const handleCancelDispensation = useCallback(async () => {
        if (!dispensationToCancel || !cancelJustification.trim()) {
            Notifications.toast(
                intl.get("CancelJustificationRequired").d("Justification is required"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        if (cancelJustification.trim().length < 10) {
            Notifications.toast(
                intl.get("JustificationTooShort").d("Justification must be at least 10 characters"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        setCanceling(true);
        try {
            const response = await fetchData(
                `/dispensing/${dispensationToCancel.pk}/request-cancellation`,
                'POST',
                true,
                { justification: cancelJustification.trim() }
            );

            if (response.status === HTTP_STATUS.CREATED_201) {
                Notifications.toast(
                    intl.get("DispensationCancellationRequested").d("Cancellation request submitted successfully. Awaiting approval."),
                    Notifications.TYPE_SUCCESS
                );
                handleCloseCancelModal();
                table.current?.updateTable();
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("DispensationCancelRequestError").d("Error submitting cancellation request"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error('Error requesting dispensation cancellation:', error);
            Notifications.toast(
                intl.get("DispensationCancelRequestError").d("Error submitting cancellation request"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setCanceling(false);
        }
    }, [dispensationToCancel, cancelJustification, handleCloseCancelModal]);

    const header = [
        {
            label: intl.get("Patient").d("Patient"),
            column_name: "patient_name",
            filter: true,
            element: (row) => row.patient_name || row.prescription?.patient?.name || '-'
        },
        {
            label: intl.get("PrescriptionNumber").d("Prescription Number"),
            column_name: "prescription_identifier",
            filter: true,
            element: (row) => row.prescription?.prescription_identifier || row.prescription_identifier || '-'
        },
        {
            label: intl.get("Institution").d("Institution"),
            column_name: "institution_name",
            filter: true,
            element: (row) => row.institution?.name || '-'
        },
        {
            label: intl.get("Batch").d("Batch"),
            column_name: "batch_number",
            filter: true,
            element: (row) => row.batch_number || row.batch?.number || '-'
        },
        {
            label: intl.get("box_count").d("Box Count"),
            column_name: "box_count",
            filter: true
        },
        {
            label: intl.get("capsule_count").d("Capsule Count"),
            column_name: "capsule_count",
            filter: true
        },
        {
            label: intl.get("DoseStrength").d("Dose Strength"),
            column_name: "dispensed_dose_strength",
            filter: true,
            element: (row) => `${row.dispensed_dose_strength} ${row.dispensed_dose_strength_unit}`
        },
        {
            label: intl.get("status").d("Status"),
            column_name: "status",
            filter: true,
            control: "select",
            options: getDispensingStatusOptions(),
            element: (row) => getStatusChip(row.status)
        },
        {
            label: intl.get("CancellationRequest").d("Cancellation Request"),
            column_name: "cancellation_request",
            sortable: false,
            filter: false,
            element: (row) => getCancellationRequestChip(row.cancellation_request)
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            element: (row) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={intl.get("ViewDetails").d("View Details")}>
                        <span>
                            <DmsButton
                                icon={<VisibilityIcon />}
                                onClick={() => handleViewDetails(row)}
                                variant="secondary"
                                size="small"
                            />
                        </span>
                    </Tooltip>
                    {row.status === DispensingStatusEnum.DISPENSED && (
                        <Tooltip title={intl.get("PrintReceipt").d("Print Receipt")}>
                            <span>
                                <DmsButton
                                    icon={<PrintIcon />}
                                    onClick={() => openDispensingReceiptForPrint(row.pk)}
                                    variant="secondary"
                                    size="small"
                                />
                            </span>
                        </Tooltip>
                    )}
                    {canCancel(row) && (
                        <Tooltip title={intl.get("RequestCancellation").d("Request Cancellation")}>
                            <span>
                                <DmsButton
                                    icon={<CancelIcon />}
                                    onClick={() => handleOpenCancelModal(row)}
                                    variant="danger"
                                    size="small"
                                />
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )
        },
    ]

    return (
        <DmsPage name={intl.get("DispensationHistory.Title").d("Dispensation History")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h6">
                        {intl.get("DispensationHistory.TableTitle").d("Dispensation Records")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={table}
                        url={'/dispensing_prescription/pharmacist/table'}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            {/* Detail Modal */}
            <DmsModal
                isVisible={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                maxWidth="md"
            >
                <DmsModal.Header
                    title={intl.get("DispensationDetails").d("Dispensation Details")}
                    onClose={() => setDetailModalOpen(false)}
                />
                <DmsModal.Body>
                    {selectedDispensation && (
                        <DmsTabber
                            tabs={[
                                {
                                    title: intl.get("Details").d("Details"),
                                    component: (
                                        <Box sx={{ p: 2 }}>
                                            {/* Header */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                                <Typography variant="h6">
                                                    {intl.get("Dispensation").d("Dispensation")} #{selectedDispensation.pk}
                                                </Typography>
                                                {getStatusChip(selectedDispensation.status)}
                                            </Box>

                                            {/* Cancellation Request Details */}
                                            {selectedDispensation.cancellation_request && (
                                                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                                    <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                        {intl.get("CancellationRequestDetails").d("Cancellation Request Details")}
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {intl.get("RequestedOn").d("Requested On")}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={8}>
                                                            <Typography variant="body2">
                                                                {selectedDispensation.cancellation_request.requested_on
                                                                    ? dayjs(selectedDispensation.cancellation_request.requested_on).format("DD/MM/YYYY HH:mm")
                                                                    : "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {intl.get("Result").d("Result")}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={8}>
                                                            {getCancellationRequestChip(selectedDispensation.cancellation_request)}
                                                        </Grid>
                                                        {selectedDispensation.cancellation_request.request_justification && (
                                                            <>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("RequestJustification").d("Request Justification")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {selectedDispensation.cancellation_request.request_justification}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {selectedDispensation.cancellation_request.reviewed_on && (
                                                            <>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("ReviewedOn").d("Reviewed On")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {dayjs(selectedDispensation.cancellation_request.reviewed_on).format("DD/MM/YYYY HH:mm")}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {selectedDispensation.cancellation_request.reviewed_by_name && (
                                                            <>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("ReviewedBy").d("Reviewed By")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {selectedDispensation.cancellation_request.reviewed_by_name}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {selectedDispensation.cancellation_request.review_justification && (
                                                            <>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("ReviewJustification").d("Review Justification")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {selectedDispensation.cancellation_request.review_justification}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            )}

                                            {/* Patient Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("Patient").d("Patient")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("Name").d("Name")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selectedDispensation.prescription?.patient?.name || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">CPF</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2">{selectedDispensation.prescription?.patient?.cpf || '-'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">ID</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2">{selectedDispensation.prescription?.patient?.patient_identifier || '-'}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            {/* Prescription Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("Prescription").d("Prescription")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("Number").d("Number")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selectedDispensation.prescription?.prescription_identifier || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("Date").d("Date")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2">
                                                            {selectedDispensation.prescription?.prescription_date
                                                                ? dayjs(selectedDispensation.prescription.prescription_date).format('DD/MM/YYYY')
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("Prescribed").d("Prescribed")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2">
                                                            {selectedDispensation.prescription?.dosage_description || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">{intl.get("ExpirationDate").d("Expiration Date")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2">
                                                            {selectedDispensation.prescription?.expiration_date
                                                                ? dayjs(selectedDispensation.prescription.expiration_date).format('DD/MM/YYYY')
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            {/* Dispensation Details */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("DispensationData").d("Dispensation Data")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    {selectedDispensation.status === DispensingStatusEnum.DISPENSED && (
                                                        <>
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("DispensedOn").d("Dispensed On")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <Typography variant="body2">
                                                                    {selectedDispensation.delivery_date
                                                                        ? dayjs(selectedDispensation.delivery_date).format('DD/MM/YYYY HH:mm')
                                                                        : (selectedDispensation.created_on
                                                                            ? dayjs(selectedDispensation.created_on).format('DD/MM/YYYY HH:mm')
                                                                            : '-')}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Box>

                                            {/* Pharmacist Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("Pharmacist").d("Pharmacist")}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={4}>
                                                        <Typography variant="caption" color="text.secondary">CRF</Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selectedDispensation.pharmacist?.crf
                                                                ? `${selectedDispensation.pharmacist.crf} (${selectedDispensation.pharmacist.crf_state})`
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            {/* Batches Section */}
                                            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                    {intl.get("Batches").d("Batches")}
                                                </Typography>
                                                {detailBatches.length > 0 ? detailBatches.map((batch, index) => (
                                                    <Paper key={index} sx={{ p: 1.5, mb: 1, border: '1px solid #e0e0e0' }} elevation={0}>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("Number").d("Number")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {batch.batch_number || '-'}
                                                                </Typography>
                                                            </Grid>
                                                            {batch.strength && (
                                                                <>
                                                                    <Grid item xs={4}>
                                                                        <Typography variant="caption" color="text.secondary">{intl.get("DoseStrength").d("Dose Strength")}</Typography>
                                                                    </Grid>
                                                                    <Grid item xs={8}>
                                                                        <Typography variant="body2">{batch.strength}</Typography>
                                                                    </Grid>
                                                                </>
                                                            )}
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("box_count").d("Box Count")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <Typography variant="body2">{batch.box_count || 0}</Typography>
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="text.secondary">{intl.get("capsule_count").d("Capsule Count")}</Typography>
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <Typography variant="body2">{batch.capsule_count || 0}</Typography>
                                                            </Grid>
                                                            {batch.batch_expiration && (
                                                                <>
                                                                    <Grid item xs={4}>
                                                                        <Typography variant="caption" color="text.secondary">{intl.get("Expiration").d("Expiration")}</Typography>
                                                                    </Grid>
                                                                    <Grid item xs={8}>
                                                                        <Typography variant="body2">
                                                                            {dayjs(batch.batch_expiration).format('DD/MM/YYYY')}
                                                                        </Typography>
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </Paper>
                                                )) : (
                                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                                )}
                                            </Box>

                                            {/* Institution Section */}
                                            {selectedDispensation.institution && (
                                                <Box sx={{ mb: 2, p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                                                    <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                        {intl.get("Institution").d("Institution")}
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {intl.get("Name").d("Name")}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={8}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {selectedDispensation.institution.name || '-'}
                                                            </Typography>
                                                        </Grid>
                                                        {selectedDispensation.institution.cnpj && (
                                                            <>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">CNPJ</Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {selectedDispensation.institution.cnpj}
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            )}

                                            {/* Justification if not dispensed */}
                                            {selectedDispensation.justification && (
                                                <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                                                    <Typography variant="subtitle2" color="error.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                        {intl.get("Justification").d("Justification")}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {selectedDispensation.justification}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )
                                },
                                {
                                    title: intl.get("History").d("History"),
                                    component: (
                                        <Box sx={{ p: 2 }}>
                                            {loadingHistory ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : historyData.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {intl.get("NoChangesRecorded").d("No changes have been made to this dispensation")}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box>
                                                    {historyData.map((record, index) => (
                                                        <Box
                                                            key={record.history_pk || index}
                                                            sx={{
                                                                mb: 2,
                                                                p: 2,
                                                                bgcolor: 'grey.50',
                                                                borderRadius: 1
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                    {intl.get("Version").d("Version")} {record.version}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {intl.get("ChangedOn").d("Changed on")}: {record.version_timestamp
                                                                        ? dayjs(record.version_timestamp).format('DD/MM/YYYY HH:mm:ss')
                                                                        : '-'}
                                                                </Typography>
                                                            </Box>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("box_count").d("Box Count")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">{record.box_count || 0}</Typography>
                                                                </Grid>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("capsule_count").d("Capsule Count")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">{record.capsule_count || 0}</Typography>
                                                                </Grid>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("DoseStrength").d("Dose Strength")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {record.dispensed_dose_strength} {record.dispensed_dose_strength_unit}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("DispensedOn").d("Dispensed On")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant="body2">
                                                                        {record.delivery_date
                                                                            ? dayjs(record.delivery_date).format('DD/MM/YYYY HH:mm')
                                                                            : (record.created_on
                                                                                ? dayjs(record.created_on).format('DD/MM/YYYY HH:mm')
                                                                                : '-')}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={4}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {intl.get("status").d("Status")}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    {getStatusChip(record.status)}
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    )
                                },
                                {
                                    title: intl.get("CancellationRequests").d("Cancellation Requests"),
                                    component: (
                                        <Box sx={{ p: 2 }}>
                                            {loadingCancellationRequests ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : cancellationRequests.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {intl.get("NoCancellationRequests").d("No cancellation requests for this dispensation")}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box>
                                                    {cancellationRequests.map((req, index) => (
                                                        <Paper
                                                            key={req.pk}
                                                            elevation={2}
                                                            sx={{
                                                                mb: 2,
                                                                p: 2,
                                                                borderLeft: 4,
                                                                borderColor: req.status === 2 ? 'success.main' : req.status === 3 ? 'error.main' : 'warning.main'
                                                            }}
                                                        >
                                                            {/* Header with status */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                    {intl.get("Request").d("Request")} #{req.pk}
                                                                </Typography>
                                                                {getCancellationRequestChip({ status: req.status })}
                                                            </Box>

                                                            {/* Request creation info */}
                                                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                                                    {intl.get("CancellationRequests.RequestCreated").d("Request Created")}
                                                                </Typography>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={4}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {intl.get("RequestedBy").d("Requested By")}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={8}>
                                                                        <Typography variant="body2">
                                                                            {req.requested_by?.user_name || '-'}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {intl.get("RequestedOn").d("Requested On")}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={8}>
                                                                        <Typography variant="body2">
                                                                            {req.requested_on ? dayjs(req.requested_on).format("DD/MM/YYYY HH:mm") : '-'}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {intl.get("Justification").d("Justification")}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={8}>
                                                                        <Typography variant="body2">
                                                                            {req.request_justification}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>

                                                            {/* Review info (if approved or rejected) */}
                                                            {req.reviewed_on && (
                                                                <Box sx={{
                                                                    p: 1.5,
                                                                    bgcolor: req.status === 2 ? 'success.50' : 'error.50',
                                                                    borderRadius: 1
                                                                }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color={req.status === 2 ? 'success.main' : 'error.main'}
                                                                        sx={{ fontWeight: 600, display: 'block', mb: 1 }}
                                                                    >
                                                                        {req.status === 2
                                                                            ? intl.get("CancellationRequests.RequestApproved").d("Request Approved")
                                                                            : intl.get("CancellationRequests.RequestRejected").d("Request Rejected")
                                                                        }
                                                                    </Typography>
                                                                    <Grid container spacing={1}>
                                                                        <Grid item xs={4}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {intl.get("ReviewedBy").d("Reviewed By")}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={8}>
                                                                            <Typography variant="body2">
                                                                                {req.reviewed_by?.user_name || '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={4}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {intl.get("ReviewedOn").d("Reviewed On")}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={8}>
                                                                            <Typography variant="body2">
                                                                                {dayjs(req.reviewed_on).format("DD/MM/YYYY HH:mm")}
                                                                            </Typography>
                                                                        </Grid>
                                                                        {req.review_justification && (
                                                                            <>
                                                                                <Grid item xs={4}>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {intl.get("ReviewJustification").d("Review Justification")}
                                                                                    </Typography>
                                                                                </Grid>
                                                                                <Grid item xs={8}>
                                                                                    <Typography variant="body2">
                                                                                        {req.review_justification}
                                                                                    </Typography>
                                                                                </Grid>
                                                                            </>
                                                                        )}
                                                                    </Grid>
                                                                </Box>
                                                            )}
                                                        </Paper>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    )
                                }
                            ]}
                        />
                    )}
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        text={intl.get("Close").d("Close")}
                        onClick={() => setDetailModalOpen(false)}
                    />
                </DmsModal.Footer>
            </DmsModal>

            {/* Cancel Dispensation Modal */}
            <DmsModal
                isVisible={cancelModalOpen}
                onClose={handleCloseCancelModal}
                maxWidth="sm"
            >
                <DmsModal.Header
                    title={intl.get("CancelDispensation").d("Cancel Dispensation")}
                    onClose={handleCloseCancelModal}
                />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        {dispensationToCancel && (
                            <>
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        {intl.get("CancelDispensationConfirm").d("Are you sure you want to cancel this dispensation?")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {intl.get("Patient").d("Patient")}: {dispensationToCancel.prescription?.patient?.name || '-'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {intl.get("Prescription").d("Prescription")}: {dispensationToCancel.prescription?.prescription_identifier || '-'}
                                    </Typography>
                                </Box>

                                <DmsTextField
                                    label={intl.get("CancelJustification").d("Cancellation justification")}
                                    value={cancelJustification}
                                    onChange={(e) => setCancelJustification(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    required
                                    placeholder={intl.get("CancelJustificationPlaceholder").d("Enter the reason for canceling this dispensation...")}
                                    helperText={intl.get("MinimumCharacters").d("Minimum 10 characters required") + ` (${cancelJustification.length}/10)`}
                                    error={cancelJustification.length > 0 && cancelJustification.length < 10}
                                />
                            </>
                        )}
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={handleCloseCancelModal}
                        variant="secondary"
                        disabled={canceling}
                    />
                    <DmsButton
                        text={intl.get("ConfirmCancellation").d("Confirm Cancellation")}
                        onClick={handleCancelDispensation}
                        variant="danger"
                        disabled={canceling || !cancelJustification.trim() || cancelJustification.trim().length < 10}
                    />
                </DmsModal.Footer>
            </DmsModal>
        </DmsPage>
    )
}
